import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, KeyRound, AlertCircle, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import { Input } from '../../Components/ui';
import { API_URL } from '../../api/config';

// Redefinição de senha em 2 passos: pede um código de 6 dígitos por e-mail
// (POST /forgot-password, resposta sempre genérica pra não revelar quais
// e-mails existem), depois troca o código + nova senha (POST /reset-password).
function EsqueciSenha() {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState('email'); // 'email' | 'codigo'
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');

  const handleEnviarCodigo = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setMensagem(data?.message || data?.data?.message || 'Código enviado.');
      setEtapa('codigo');
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível enviar o código. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const handleRedefinirSenha = async (e) => {
    e.preventDefault();
    setErro('');
    if (novaSenha.length < 6) return setErro('A nova senha deve ter pelo menos 6 caracteres.');
    if (novaSenha !== confirmarSenha) return setErro('As senhas não coincidem.');
    setCarregando(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { email, codigo, novaSenha });
      navigate('/login', {
        replace: true,
        state: { mensagem: 'Senha redefinida com sucesso. Faça login com sua nova senha.' },
      });
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível redefinir a senha.');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#F9FAFB] to-[#E5E7EB] dark:from-gray-950 dark:to-gray-900 p-4">
      <div className="w-full max-w-md bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/50 dark:border-gray-800/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] p-4">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="Portal Novas Histórias" className="w-20 h-auto object-contain mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
            Redefinir senha
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center">
            {etapa === 'email'
              ? 'Informe seu e-mail para receber um código de verificação.'
              : 'Digite o código recebido e escolha uma nova senha.'}
          </p>
        </div>

        {mensagem && etapa === 'codigo' && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400 text-sm mb-4">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{mensagem}</span>
          </div>
        )}
        {erro && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-600 dark:text-red-400 text-sm mb-4">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        {etapa === 'email' ? (
          <form className="space-y-4" onSubmit={handleEnviarCodigo}>
            <Input
              label="E-mail"
              type="email"
              size="lg"
              icon={Mail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="seu@email.com"
              required
            />
            <button
              type="submit"
              disabled={carregando}
              className="w-full h-11 mt-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {carregando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                </>
              ) : (
                'Enviar código'
              )}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleRedefinirSenha}>
            <Input
              label="Código de 6 dígitos"
              type="text"
              size="lg"
              icon={KeyRound}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              required
            />
            <Input
              label="Nova senha"
              type="password"
              size="lg"
              icon={Lock}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
              required
              minLength={6}
            />
            <Input
              label="Confirmar nova senha"
              type="password"
              size="lg"
              icon={Lock}
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
              required
              minLength={6}
            />
            <button
              type="submit"
              disabled={carregando}
              className="w-full h-11 mt-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {carregando ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Redefinindo...
                </>
              ) : (
                'Redefinir senha'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setEtapa('email');
                setErro('');
              }}
              className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors"
            >
              Não recebeu? Pedir um novo código
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default EsqueciSenha;
